// ABOUTME: /software feedback triage list (ShDataGrid2): reference id, category, status, severity, date, duplicate badge.
// ABOUTME: Filters (category / date range / text search) + 20/page pagination feed the getAdminFeedback query; rows open the detail panel.

import { Box, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Text from "../../../../components/theme/text/Text";
import ShLabel from "../../../../components/theme/label/ShLabel";
import ShDataGrid2 from "../../../../components/theme/datagrid2/ShDataGrid2";
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader";
import ShTextSkeleton from "../../../../components/theme/skeleton/ShTextSkeleton";
import { useGetAdminFeedbackQuery } from "../../../../newapi/feedback/feedbackApiSlice";
import {
    toReferenceId,
    triageStatusKey,
    triageStatusVariant,
    severityVariant,
} from "../feedbackTriage";

const SpFeedbackDetails = SidepanelLoader(() => import("../SpFeedbackDetails"));

const gridOptions = {
    export: false,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
    // Category + free-text search are applied via the grid filter; the date range is
    // forwarded through the filter body to the getAdminFeedback query string.
    filter: {
        name: "feedbackFilter",
        selectedFilter: null,
        groups: [{ conditionExtensionType: 0, rules: [] }],
    },
};

const All = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [isExtended, setIsExtended] = useState(false);
    const [spLoading, setSpLoading] = useState(false);

    const [feedbackId, setFeedbackId] = useState();
    const feedbackIdRef = useRef(feedbackId);

    const getFeedbackDetails = (params) => {
        if (!params || params.row.skeleton) return;

        const newId = params.row.id;
        if (feedbackIdRef.current !== newId) {
            if (!spLoading) {
                setSpLoading(true);
            }
            setFeedbackId(newId);
            feedbackIdRef.current = newId;
        }
        setIsExtended(true);
    };

    useEffect(() => {
        props.replaceView({
            ...props.config,
            gridOptions: gridOptions,
        });
    }, [props]);

    const columns = [
        {
            field: "id",
            headerName: t("feedback_reference"),
            flex: 1,
            sortable: false,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) return <ShTextSkeleton />;
                return <Text semibold>{toReferenceId(cellValues.row.id)}</Text>;
            },
        },
        {
            field: "category",
            headerName: t("feedback_category"),
            flex: 1,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) return <ShTextSkeleton />;
                return cellValues.row.category;
            },
        },
        {
            field: "reporterEmail",
            headerName: t("feedback_reporter"),
            flex: 1,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) return <ShTextSkeleton />;
                return cellValues.row.reporterName || cellValues.row.reporterEmail || "—";
            },
        },
        {
            field: "triageStatus",
            headerName: t("feedback_triage_status"),
            flex: 1,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) return <ShTextSkeleton />;
                return (
                    <ShLabel size={32} fitted variant={triageStatusVariant(cellValues.row.triageStatus)}>
                        {t(triageStatusKey(cellValues.row.triageStatus))}
                    </ShLabel>
                );
            },
        },
        {
            field: "severity",
            headerName: t("feedback_severity"),
            flex: 1,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) return <ShTextSkeleton />;
                if (!cellValues.row.severity) return <Text light>—</Text>;
                return (
                    <ShLabel size={32} fitted variant={severityVariant(cellValues.row.severity)}>
                        {cellValues.row.severity}
                    </ShLabel>
                );
            },
        },
        {
            field: "createdAt",
            headerName: t("date"),
            flex: 1,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) return <ShTextSkeleton />;
                const d = cellValues.row.createdAt ? new Date(cellValues.row.createdAt) : null;
                return d ? d.toLocaleDateString() : "—";
            },
        },
        {
            field: "duplicateCount",
            headerName: t("feedback_duplicate_count"),
            flex: 1,
            sortable: false,
            renderCell: (cellValues) => {
                if (cellValues.row.skeleton) return <ShTextSkeleton />;
                const count = cellValues.row.duplicateCount ?? 0;
                if (count <= 0) return <Text light>—</Text>;
                return (
                    <ShLabel size={32} fitted variant="purple">
                        {count}
                    </ShLabel>
                );
            },
        },
    ];

    return (
        <Box
            sx={{
                height: 1,
                "@media screen and (max-width: 47.9375em)": {
                    paddingTop: 0,
                    paddingX: 0,
                },
            }}
        >
            <ShDataGrid2
                key={theme.palette.mode}
                content={useGetAdminFeedbackQuery}
                title={t("overview")}
                gridOptions={gridOptions}
                columns={columns}
                sortModel={{ field: "createdAt", type: "DESC" }}
                onRowClick={getFeedbackDetails}
            />

            {spLoading && (
                <SpFeedbackDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    feedbackId={feedbackId}
                />
            )}
        </Box>
    );
};

export default All;
